import BigNumber from "bignumber.js";
import { ActionCableService, sharePublishReplay, swalloError } from "ngx-libs";
import { BehaviorSubject, combineLatest, Observable, of, timer } from "rxjs";
import { debounceTime, distinctUntilChanged, map, shareReplay, startWith, switchMap } from "rxjs/operators";
import { keepUpdater, unsubscribeLater } from "../rxjs";
import { EthWalletService } from "../services/eth-wallet.service";
import { MainService } from "../services/main.service";
import { Market } from "./market";
import { Strike } from "./strike";

export class Expiry {
  settledPrice: Observable<BigNumber>;
  expired: Observable<boolean>;
  strikes: Observable<Strike[]>;
  disabled: Observable<boolean>;

  private dataSubject = new BehaviorSubject<any>({});
  private strikesCache: Map<string, Strike> = new Map();
  private settledPriceSubject = new BehaviorSubject(new BigNumber(0));

  constructor(
    public timestamp: number,
    private ethWalletService: EthWalletService,
    private actionCableService: ActionCableService,
    private mainService: MainService,
    private market: Market
  ) {
    this.settledPrice = this.settledPriceSubject.asObservable();

    // this.ethWalletService.blockNumber
    //   .pipe(
    //     takeUntil(this.settledPrice.pipe(skip(1))),
    //     switchMap(() =>
    //       this.spotPricer.pipe(
    //         mergeMap((spotPricer) => spotPricer.getSettledPrice(expiry.timestamp)),
    //         filter((p) => !p.eq(0))
    //       )
    //     )
    //   )
    //   .subscribe((p) => this.settledPriceSubject.next(p));

    this.disabled = this.dataSubject.pipe(
      map((data) => data.disabled)
    );

    const now = this.market.vault.now;
    this.expired = now.pipe(
      switchMap((t) => {
        if (t > this.timestamp) {
          return this.settledPrice.pipe(map((p) => !p.eq(0)));
        } else {
          return of(false);
        }
      }),
      distinctUntilChanged(),
      sharePublishReplay(1)
    );

    const received = this.actionCableService.received({
      channel: 'StrikesChannel',
      expiry: this.timestamp
    }).pipe(
      startWith(0),
      debounceTime(10)
    );

    this.strikes = combineLatest([received, timer(0, 60000)]).pipe(
      debounceTime(20),
      keepUpdater(),
      switchMap(() => this.mainService.markets.of(this.market.name).expiries.of(this.timestamp).strikes.query().pipe(
        swalloError(),
        unsubscribeLater(5000),
        shareReplay(1)
      )),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      map((json) => json.strikes.map((p: any) => this.processStrike(p))),
      sharePublishReplay(1)
    );
  }

  get data() {
    return this.dataSubject.value;
  }

  updateData(data: any) {
    this.market.contractVault.optionPricer.blackScholesLookup.sqrtTs[this.timestamp] = data.sqrtTs.toDecimal();
    this.market.contractVault.optionPricer.blackScholesLookup.pvs[this.timestamp] = data.pvs.toDecimal();
    this.dataSubject.next(data);
  }

  private processStrike(record: any) {
    const strike = this.getStrikeCache(record.price);
    strike.updateData(record);
    return strike;
  }

  private getStrikeCache(price: BigNumber) {
    const key = price.toString(10);
    if (!this.strikesCache.has(key)) {
      this.strikesCache.set(key, new Strike(price, this.ethWalletService, this.market, this));
    }
    return this.strikesCache.get(key);
  }
}
